import * as React from 'react';
import { Box, Select, VisuallyHidden } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { RemovableChip } from './removable-chip.component';
import type { CatalogOption } from './source-resolution';

interface PillProps {
  /**
   * The comparison-value options the picker offers. Pre-resolved by the
   * condition item off the enclosing item via the source's
   * `valueCatalog` (see `resolveCatalogOptions`) — the picker just
   * renders them.
   */
  options: CatalogOption[];
  /**
   * Full entity edit path of the condition this picker belongs to — the
   * base for the rendered field's unique `id`/`name` (a11y/autofill).
   */
  editPath: string;
  enabled: boolean;
}

export interface MultiValuePickerProps extends PillProps {
  value: string[] | undefined;
  onChange: (next: string[]) => void;
}

/**
 * Multi-select rendered as a row of pill toggles + a "+ Add" select for new
 * entries. Avoids needing a fancy multi-select primitive; each chosen value
 * is a clickable chip that removes itself.
 */
export const MultiValuePicker: React.FC<MultiValuePickerProps> = ({
  value,
  onChange,
  options,
  editPath,
  enabled,
}) => {
  const { formatMessage } = useIntl();
  const selected = value ?? [];
  const remaining = options.filter(
    (option) => !selected.includes(option.value),
  );
  const labelFor = (code: string) =>
    options.find((option) => option.value === code)?.label ?? code;
  const fieldId = `${editPath}.inputs.values`;
  // Removing a chip removes the focused element from the DOM — move
  // focus to the persistent add-select so keyboard users aren't dropped
  // to <body> (WCAG 2.4.3).
  const addSelectRef = React.useRef<HTMLSelectElement | null>(null);

  // Placeholder explains why the add-select is empty/disabled.
  let addPlaceholderKey = 'CONDITIONAL_ACTION_ADD_VALUE';
  if (options.length === 0) {
    addPlaceholderKey = 'CONDITIONAL_ACTION_NO_VALUE_OPTIONS_AVAILABLE';
  } else if (remaining.length === 0) {
    addPlaceholderKey = 'CONDITIONAL_ACTION_ALL_OPTIONS_SELECTED';
  }

  return (
    <Box
      css={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '$half',
      }}
    >
      {selected.map((code) => (
        <RemovableChip
          key={code}
          label={labelFor(code)}
          removeLabel={formatMessage(
            { id: 'CONDITIONAL_ACTION_REMOVE_VALUE_ARIA' },
            { code: labelFor(code) },
          )}
          enabled={enabled}
          onRemove={() => {
            onChange(selected.filter((c) => c !== code));
            addSelectRef.current?.focus();
          }}
        />
      ))}
      {/* Always render the add-select. When there are no remaining options
          (either the source has no options yet, or all of them are
          already selected), keep the picker visible but disabled with a
          helpful placeholder so authors can see why nothing's selectable. */}
      <VisuallyHidden as="label" htmlFor={fieldId}>
        {formatMessage({ id: 'CONDITIONAL_ACTION_ADD_VALUE' })}
      </VisuallyHidden>
      <Select
        ref={addSelectRef}
        id={fieldId}
        name={fieldId}
        value=""
        onChange={(evt) => {
          const next = evt.currentTarget.value;
          if (next) onChange([...selected, next]);
        }}
        disabled={!enabled || remaining.length === 0}
        options={[
          {
            value: '',
            label: formatMessage({ id: addPlaceholderKey }),
          },
          ...remaining,
        ]}
      />
    </Box>
  );
};
